import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Target, 
  AlertCircle, 
  CheckCircle2, 
  Save,
  Info,
  Settings2,
  ArrowUp,
  Layout
} from "lucide-react";
import { useDispatch } from "react-redux";
import { updateProposedScoping } from "../../redux/slices/analysisSlice";

/**
 * ProjectScopingTool - A comprehensive tool for testing building size, height, and setbacks.
 */
const ProjectScopingTool = ({ 
  analysisId,
  mfa = 0, 
  maxHeight = 0,
  setbackFront = 0,
  setbackRear = 0,
  setbackSide = 0,
  livingArea = 0,
  grossBuildingArea = 0,
  currentProposedGfa = 0, 
  currentProposedHeight = 0,
  currentProposedSetbackFront = 0,
  currentProposedSetbackRear = 0,
  currentProposedSetbackSide = 0,
  totalPlotted = 0,
  compliance = null,
  mfaLabel = "MFA",
}) => {
  const dispatch = useDispatch();
  
  // State for all scoping targets
  const [scoping, setScoping] = useState({
    proposed_gfa_ft2: currentProposedGfa || 0,
    proposed_height_ft: currentProposedHeight || 0,
    proposed_setback_front_ft: currentProposedSetbackFront || 0,
    proposed_setback_rear_ft: currentProposedSetbackRear || 0,
    proposed_setback_side_ft: currentProposedSetbackSide || 0
  });

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Sync with props if they change externally
  useEffect(() => {
    setScoping({
      proposed_gfa_ft2: currentProposedGfa || 0,
      proposed_height_ft: currentProposedHeight || 0,
      proposed_setback_front_ft: currentProposedSetbackFront || 0,
      proposed_setback_rear_ft: currentProposedSetbackRear || 0,
      proposed_setback_side_ft: currentProposedSetbackSide || 0
    });
  }, [currentProposedGfa, currentProposedHeight, currentProposedSetbackFront, currentProposedSetbackRear, currentProposedSetbackSide]);

  // Debounced save to backend
  useEffect(() => {
    const hasChanged = JSON.stringify(scoping) !== JSON.stringify({
      proposed_gfa_ft2: currentProposedGfa || 0,
      proposed_height_ft: currentProposedHeight || 0,
      proposed_setback_front_ft: currentProposedSetbackFront || 0,
      proposed_setback_rear_ft: currentProposedSetbackRear || 0,
      proposed_setback_side_ft: currentProposedSetbackSide || 0
    });

    if (!hasChanged) return;

    const timer = setTimeout(() => {
      setIsSaving(true);
      dispatch(updateProposedScoping({ analysisId, data: scoping }))
        .finally(() => setIsSaving(false));
    }, 12 * 100); // 1.2s

    return () => clearTimeout(timer);
  }, [scoping, analysisId, dispatch, currentProposedGfa, currentProposedHeight, currentProposedSetbackFront, currentProposedSetbackRear, currentProposedSetbackSide]);

  const handleValueChange = (field, val) => {
    setScoping(prev => ({ ...prev, [field]: val }));
  };

  const isGfaOver = scoping.proposed_gfa_ft2 > mfa;
  const isHeightOver = scoping.proposed_height_ft > (maxHeight || Infinity);

  // Setback compliance: proposed must be GREATER THAN or EQUAL TO legal min
  const isFrontViolated = scoping.proposed_setback_front_ft < (setbackFront || 0);
  const isRearViolated = scoping.proposed_setback_rear_ft < (setbackRear || 0);
  const isSideViolated = scoping.proposed_setback_side_ft < (setbackSide || 0);

    
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/80 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl shadow-slate-200/40 p-6 overflow-hidden relative group"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-500/10 flex items-center justify-center">
            <Target className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-sm font-black text-slate-800 uppercase tracking-tighter">Project Scoping</h3>
            <p className="text-[10px] font-bold uppercase tracking-widest">
              <span className="text-blue-800">
                {livingArea?.toLocaleString()} sqft {livingArea !== grossBuildingArea ? "(Floor Area)" : "(Footprint)"}
              </span>
              {livingArea !== grossBuildingArea && grossBuildingArea > 0 && (
                <>
                  <span className="text-slate-400 mx-1.5">•</span>
                  <span className="text-indigo-400">
                    {grossBuildingArea?.toLocaleString()} sqft (Footprint)
                  </span>
                </>
              )}
              <span className="text-slate-400 mx-1.5">•</span>
              <span className="text-emerald-800">
                Max: {mfa?.toLocaleString()} sqft ({mfaLabel})
              </span>
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <AnimatePresence>
            {isSaving && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="flex items-center gap-1.5 px-2 py-1 bg-indigo-50 rounded-full border border-indigo-100"
              >
                <motion.div 
                  animate={{ rotate: 360 }}
                  transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                >
                  <Save className="w-3 h-3 text-indigo-500" />
                </motion.div>
                <span className="text-[9px] font-bold text-indigo-600 uppercase">Syncing...</span>
              </motion.div>
            )}
          </AnimatePresence>

          <button 
            onClick={() => setShowAdvanced(!showAdvanced)}
            className={`flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all min-w-[120px] ${
              showAdvanced ? "bg-slate-200 text-slate-700" : "bg-white text-slate-400 border border-slate-100"
            }`}
          >
            <Settings2 className="w-3 h-3" />
            {showAdvanced ? "Basic View" : "Advanced Scoping"}
          </button>
        </div>
      </div>

      {/* Main Scoping Section (GFA) */}
      <div className="mb-10">
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-2">
            <Layout className="w-4 h-4 text-slate-400" />
            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Building Size ({mfaLabel})</label>
            <div className="flex items-center gap-1.5 px-2 py-0.5 mb-2 rounded-md bg-emerald-50 border border-emerald-100 shadow-sm mt-1">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
              <span className="text-[8px] font-black text-emerald-800 uppercase tracking-widest leading-none">
                Potential Expansion: {Math.max(0, mfa - livingArea).toLocaleString()} SQFT (Floor Area)
              </span>
            </div>
          </div>
          <div className="text-right flex flex-col items-end">
            <div>
              <span className={`text-2xl font-black tracking-tighter ${isGfaOver ? 'text-rose-500' : 'text-slate-800'}`}>
                {Math.round(scoping.proposed_gfa_ft2).toLocaleString()}
              </span>
              <span className="text-[10px] font-bold text-slate-400 ml-1 uppercase">sqft</span>
            </div>
          </div>
        </div>
        
        <Slider 
          value={scoping.proposed_gfa_ft2}
          max={Math.max(mfa * 1.5, scoping.proposed_gfa_ft2 + 1000, 5000)}
          limit={mfa}
          marker={totalPlotted}
          markerLabel="Draft Drawing"
          limitLabel="Zoning Limit"
          existing={livingArea}
          existingLabel={(grossBuildingArea > 0 && livingArea === grossBuildingArea) ? "Existing Footprint" : "Assessor Living Area"}
          onChange={(v) => handleValueChange('proposed_gfa_ft2', v)}
          isOver={isGfaOver}
        />
      </div>

      {/* Advanced Scoping (Height & Setbacks) */}
      <AnimatePresence>
        {showAdvanced && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden space-y-8 border-t border-slate-100 pt-8"
          >
            {/* Height Scoping */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <ArrowUp className="w-4 h-4 text-slate-400" />
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Building Height</label>
                </div>
                <div className="text-right">
                  <span className={`text-2xl font-black tracking-tighter ${isHeightOver ? 'text-rose-500' : 'text-slate-800'}`}>
                    {Math.round(scoping.proposed_height_ft)}
                  </span>
                  <span className="text-[10px] font-bold text-slate-400 ml-1 uppercase">ft</span>
                </div>
              </div>
              <Slider 
                value={scoping.proposed_height_ft}
                max={Math.max((maxHeight || 35) * 1.5, scoping.proposed_height_ft + 10, 50)}
                limit={maxHeight}
                limitLabel="Height Limit"
                onChange={(v) => handleValueChange('proposed_height_ft', v)}
                isOver={isHeightOver}
                colorClass="bg-indigo-400"
              />
            </div>

            {/* Setbacks Group */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <SetbackInput 
                label="Front Setback" 
                value={scoping.proposed_setback_front_ft}
                limit={setbackFront}
                isViolated={isFrontViolated}
                onChange={(v) => handleValueChange('proposed_setback_front_ft', v)}
              />
              <SetbackInput 
                label="Rear Setback" 
                value={scoping.proposed_setback_rear_ft}
                limit={setbackRear}
                isViolated={isRearViolated}
                onChange={(v) => handleValueChange('proposed_setback_rear_ft', v)}
              />
              <SetbackInput 
                label="Side Setback" 
                value={scoping.proposed_setback_side_ft}
                limit={setbackSide}
                isViolated={isSideViolated}
                onChange={(v) => handleValueChange('proposed_setback_side_ft', v)}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Status Summary */}
      <div className={`mt-8 flex items-start gap-3 px-5 py-4 rounded-2xl border transition-all duration-500 overflow-hidden relative ${
        compliance?.status === "NON_COMPLIANT" 
          ? "bg-rose-50 border-rose-100 text-rose-700 shadow-rose-100/50 shadow-lg" 
          : compliance?.status === "REVIEW_REQUIRED"
            ? "bg-amber-50 border-amber-100 text-amber-700 shadow-amber-100/50 shadow-lg"
            : "bg-emerald-50 border-emerald-100 text-emerald-700 shadow-emerald-100/30 shadow-lg"
      }`}>
        {/* Animated Background Pulse for attention */}
        {compliance?.status !== "COMPLIANT" && (
          <motion.div 
            animate={{ opacity: [0.3, 0.6, 0.3] }}
            transition={{ repeat: Infinity, duration: 3 }}
            className={`absolute top-0 left-0 w-1 h-full ${
              compliance?.status === "NON_COMPLIANT" ? "bg-rose-500" : "bg-amber-500"
            }`}
          />
        )}

        <div className="mt-0.5">
          {compliance?.status === "NON_COMPLIANT" ? (
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
          ) : compliance?.status === "REVIEW_REQUIRED" ? (
            <Info className="w-5 h-5 flex-shrink-0" />
          ) : (
            <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
          )}
        </div>

        <div className="flex-1">
          <div className="flex items-center justify-between mb-1">
            <div className="font-black uppercase tracking-widest text-[10px]">
              {compliance?.status === "NON_COMPLIANT" 
                ? "Municipal Standards Not Met" 
                : compliance?.status === "REVIEW_REQUIRED"
                  ? "Entitlement Review Required"
                  : "Feasibility Target Confirmed"
              }
            </div>
          </div>
          
          <div className="space-y-2">
            <span className="opacity-90 font-bold text-xs leading-tight block">
              {compliance?.status === "COMPLIANT" 
                ? `Your proposed project intent is fully compliant with municipal building size, height, and setback limits.`
                : compliance?.status === "REVIEW_REQUIRED"
                  ? "This configuration requires a Conditional Use Permit or Design Review due to its scale or hillside location."
                  : "One or more of your targets exceed municipal development standards."
              }
            </span>

            {/* Render Violation List */}
            <AnimatePresence>
              {compliance?.violations?.length > 0 && (
                <motion.ul 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  className="space-y-1.5 pt-2 border-t border-rose-200/50"
                >
                  {compliance.violations.map((v, i) => (
                    <motion.li 
                      key={i}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.1 }}
                      className="text-[11px] font-medium flex items-start gap-2"
                    >
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-rose-400 flex-shrink-0" />
                      {v}
                    </motion.li>
                  ))}
                </motion.ul>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-1.5 text-[9px] text-slate-400 uppercase font-black tracking-widest pl-1">
        <Info className="w-2.5 h-2.5" />
        <span>Scoping targets are persisted to your project file</span>
      </div>
    </motion.div>
  );
};// Internal Slider Component for cleaner code
const Slider = ({ value, max, limit, marker, markerLabel, limitLabel, existing, existingLabel, onChange, isOver }) => {
  const safeMax = max > 0 ? max : 1;
  const existingPct = (existing / safeMax) * 100;
  const limitPct = (limit / safeMax) * 100;
  const valuePct = (value / safeMax) * 100;
  
  const fillGradient = `linear-gradient(to right, 
    #3b82f6 0%, 
    #3b82f6 ${existingPct}%, 
    #10b981 ${existingPct}%, 
    #10b981 ${limitPct}%, 
    #f43f5e ${limitPct}%
  )`;

  return (
    <div className="relative pt-4">

      <div className="h-2 w-full bg-slate-200 rounded-full overflow-hidden border border-slate-300 relative">
        {/* Zone 1: Existing footprint background shading - Dark Blue */}
        {existing > 0 && (
           <div 
             className="absolute top-0 left-0 h-full bg-blue-700 z-0"
             style={{ width: `${existingPct}%` }}
           />
        )}
        
        {/* Zone 2: Zoning Limit background shading - Dark Emerald */}
        {limit > existing && (
           <div 
             className="absolute top-0 h-full bg-emerald-700 z-0"
             style={{ 
               left: `${existingPct}%`,
               width: `${limitPct - existingPct}%` 
             }}
           />
        )}

        <motion.div 
          animate={{ width: `${Math.min(100, valuePct)}%` }}
          className="absolute top-0 left-0 h-full z-10 border-r-2 border-white/40"
          style={{ background: fillGradient }}
          transition={{ type: "spring", stiffness: 100, damping: 20 }}
        />
      </div>


      {/* Existing Footprint Marker - Dark Blue */}
      {existing > 0 && (
        <div className="absolute top-0 h-6 w-0.5 bg-blue-900 flex flex-col items-center group/existing" style={{ left: `${existingPct}%` }}>
          <div className="absolute top-[14px] w-3 h-3 rounded-full bg-blue-900 shadow-[0_0_0_2px_#fff] z-20" />
          <span className="absolute -top-6 text-[8px] font-black text-white uppercase bg-blue-900 px-1.5 py-0.5 rounded shadow-sm whitespace-nowrap z-30">
            {existingLabel}: {existing.toLocaleString()}
          </span>
        </div>
      )}

      {/* Legal Limit Marker - Dark Emerald */}
      {limit > 0 && (
        <div className="absolute top-0 h-6 w-0.5 bg-emerald-900 flex flex-col items-center group/marker" style={{ left: `${limitPct}%` }}>
          <div className="absolute top-[14px] w-3 h-3 rounded-full bg-emerald-900 shadow-[0_0_0_2px_#fff] z-20" />
          <span className="absolute -top-6 text-[8px] font-black text-white uppercase bg-emerald-900 px-1.5 py-0.5 rounded shadow-sm whitespace-nowrap z-30">
            {limitLabel}: {limit}
          </span>
        </div>
      )}

      {/* Secondary Marker (e.g. Plotted Area) - Default color */}
      {marker > 0 && marker !== value && (
        <div className="absolute top-0 h-6 w-0.5 bg-slate-400 flex flex-col items-center group/plotted" style={{ left: `${(marker / safeMax) * 100}%` }}>
          <div className="absolute -top-1 w-2 h-2 rounded-full bg-slate-400" />
          <span className="absolute -top-8 text-[8px] font-black text-white bg-slate-500 px-1.5 py-0.5 rounded shadow-sm opacity-0 group-hover/plotted:opacity-100 transition-all whitespace-nowrap z-30">
            {markerLabel}: {marker.toLocaleString()}
          </span>
        </div>
      )}

      <input
        type="range" min="0" max={max} step={max < 100 ? 0.5 : 10}
        value={value} onChange={(e) => onChange(parseFloat(e.target.value))}
        className="absolute top-3.5 left-0 w-full h-3 bg-transparent appearance-none cursor-pointer z-10"
        style={{ WebkitAppearance: "none", outline: "none" }}
      />
    </div>
  );
};



// Internal Setback Input Component
const SetbackInput = ({ label, value, limit, isViolated, onChange }) => (
  <div className="space-y-3">
    <div className="flex items-center justify-between">
      <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{label}</label>
      <span className={`text-sm font-black ${isViolated ? "text-rose-500" : "text-slate-700"}`}>
        {value} <span className="text-[10px] text-slate-400 font-bold uppercase">ft</span>
      </span>
    </div>
    <div className="relative pt-1">
      <input 
        type="range" min="0" max="60" step="1" value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-1.5 bg-slate-100 rounded-full appearance-none cursor-pointer"
        style={{ WebkitAppearance: "none", outline: "none" }}
      />
      {limit > 0 && (
        <div className="absolute top-0 h-4 border-l border-slate-300 pointer-events-none" style={{ left: `${(limit / 60) * 100}%` }}>
          <span className="absolute -top-4 text-[8px] font-black text-slate-300 uppercase whitespace-nowrap left-1">Min: {limit}ft</span>
        </div>
      )}
    </div>
  </div>
);

export default ProjectScopingTool;
